import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import PackageForm from './PackageForm';
import PackageList from './PackageList';
import StatsCards from './StatsCards';
import CameraScanner from './CameraScanner';
import { celebratePickup, quickBurst } from '../utils/confetti';
import soundEffects from '../utils/sounds';
import { Boxy, Checky, Noti, Scannie, CartoonStyles } from './CartoonCharacters';
import adminApi from '../utils/adminApi';
import { startAutoBackup, stopAutoBackup } from '../utils/autoBackup';
import AshopClock from './AshopClock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffDashboard = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [stats, setStats] = useState({ total_packages: 0, active_packages: 0, picked_up_today: 0 });
  const [inboxUnread, setInboxUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [activeTab, setActiveTab] = useState('total'); // 'total', 'available', 'picked_up', or 'history'
  const [showCustomNumberDialog, setShowCustomNumberDialog] = useState(false);
  const [showResetConfirmDialog, setShowResetConfirmDialog] = useState(false);
  const [showResetCounterDialog, setShowResetCounterDialog] = useState(false);
  const [customBoxNumber, setCustomBoxNumber] = useState('');
  const [resetCounterNumber, setResetCounterNumber] = useState('1');
  const [boxNumberState, setBoxNumberState] = useState({ current: 0, initialized: false });
  
  // Box number initialization
  const [showBoxInitDialog, setShowBoxInitDialog] = useState(false);
  const [boxStartNumber, setBoxStartNumber] = useState('1');
  const [boxNumberInitialized, setBoxNumberInitialized] = useState(false);
  
  // Notification system
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);
  const audioRef = useRef(null);
  const pollingRef = useRef(null);
  const inventoryRef = useRef(null); // Ref for scrolling to inventory
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);

  // NEW: Advanced filtering states
  const [ageFilter, setAgeFilter] = useState('all'); // 'all', 'today', 'week', 'old'
  const [carrierFilter, setCarrierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'available', 'picked_up'

  // NEW: 12+ day old packages warning system
  const [showOldPackagesWarning, setShowOldPackagesWarning] = useState(false);
  const [oldPackages, setOldPackages] = useState([]);
  const [lastWarningDate, setLastWarningDate] = useState(null);

  // NEW: History tab
  const [historyPackages, setHistoryPackages] = useState([]);
  const [showClearHistoryDialog, setShowClearHistoryDialog] = useState(false);
  const [ratingStats, setRatingStats] = useState({ average: 0, count: 0 });
  const [showCartoon, setShowCartoon] = useState(null);
  const [resetCustomNumber, setResetCustomNumber] = useState(''); // 'boxy' | 'checky' | 'noti' | null

  // NEW: 3rd Party Pickup
  const [show3rdPartyPickupModal, setShow3rdPartyPickupModal] = useState(false);
  const [thirdPartyForm, setThirdPartyForm] = useState({
    boxNumber: '',
    pickerName: '',
    pickerId: '',
    pickerPhone: ''
  });
  const [thirdPartyPrefillData, setThirdPartyPrefillData] = useState(null); // Store prefill data from notification

  // PIN Protection for management buttons
  const [showManagementPinModal, setShowManagementPinModal] = useState(false);
  const [managementPinInput, setManagementPinInput] = useState('');
  const [managementPinError, setManagementPinError] = useState('');
  const [pendingAction, setPendingAction] = useState(null); // 'set_box', 'reset', 'clear_all'

  // Check box number initialization on load
  useEffect(() => {
    checkBoxNumberStatus();
    startAutoBackup();
    // Poll suggestions unread count so the badge stays fresh
    const loadInbox = async () => {
      try {
        const r = await adminApi.get('/suggestions');
        setInboxUnread(r.data?.unread || 0);
      } catch { /* silent */ }
    };
    loadInbox();
    const inboxId = setInterval(loadInbox, 30 * 1000);
    return () => { stopAutoBackup(); clearInterval(inboxId); };
  }, []);

  const checkBoxNumberStatus = async () => {
    try {
      const response = await axios.get(`${API}/box-number/current`);
      setBoxNumberInitialized(response.data.initialized);
      if (!response.data.initialized) {
        setShowBoxInitDialog(true);
      }
    } catch (error) {
      console.error('Error checking box number status:', error);
    }
  };

  const handleBoxNumberInit = async () => {
    try {
      const startNum = parseInt(boxStartNumber);
      if (isNaN(startNum) || startNum < 1) {
        toast.error('Please enter a valid number (1 or greater)');
        return;
      }

      await adminApi.post(`${API}/box-number/initialize?start_number=${startNum}`);
      setBoxNumberInitialized(true);
      setShowBoxInitDialog(false);
      setBoxNumberState({ current: startNum, initialized: true });
      toast.success(`Box numbering initialized! Starting from ${startNum}`);
      fetchPackages(); // Refresh to get updated stats
    } catch (error) {
      console.error('Error initializing box numbers:', error);
      toast.error('Failed to initialize box numbering');
    }
  };

  const handleResetBoxNumber = async () => {
    try {
      await adminApi.post(`${API}/box-number/reset?start_number=1`);
      setShowResetConfirmDialog(false);
      setBoxNumberState({ current: 1, initialized: true });
      toast.success('Box numbering reset! Starting from 1');
      fetchPackages(); // Refresh to get updated stats
    } catch (error) {
      console.error('Error resetting box numbers:', error);
      toast.error('Failed to reset box numbering');
    }
  };

  const handleSetCustomNumber = async () => {
    try {
      const customNum = parseInt(customBoxNumber);
      if (isNaN(customNum) || customNum < 1) {
        toast.error('Please enter a valid number (1 or greater)');
        return;
      }

      await adminApi.post(`${API}/box-number/reset?start_number=${customNum}`);
      setShowCustomNumberDialog(false);
      setCustomBoxNumber('');
      setBoxNumberState({ current: customNum, initialized: true });
      toast.success(`Box numbering set! Next box will be ${customNum}`);
      fetchPackages(); // Refresh to get updated stats
    } catch (error) {
      console.error('Error setting custom box number:', error);
      toast.error('Failed to set custom box number');
    }
  };


  const handleResetTotalScannedCounter = async () => {
    try {
      const startNum = parseInt(resetCounterNumber);
      if (isNaN(startNum) || startNum < 0) {
        toast.error('Please enter a valid number (0 or greater)');
        return;
      }

      await adminApi.post(`${API}/total-scanned/reset?start_number=${startNum}`);
      setShowResetCounterDialog(false);
      setResetCounterNumber('1');
      toast.success(`Total Scanned counter reset! Starting from ${startNum}`);
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error resetting total scanned counter:', error);
      toast.error('Failed to reset counter');
    }
  };


  // Notification polling
  useEffect(() => {
    // Start polling for notifications
    const startPolling = () => {
      pollingRef.current = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/notifications/unread`);
          if (response.data.notifications && response.data.notifications.length > 0) {
            const latestNotification = response.data.notifications[0];
            setCurrentNotification(latestNotification);
            setShowNotificationPopup(true);
            flashCartoon('noti');
            
            // Play sound alert
            if (audioRef.current) {
              audioRef.current.play().catch(err => console.log('Audio play failed:', err));
            }
            
            // DON'T mark as read yet - wait for admin to act on it
          }
        } catch (error) {
          console.error('Error polling notifications:', error);
        }
      }, 3000); // Poll every 3 seconds
    };

    startPolling();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const closeNotificationPopup = () => {
    setShowNotificationPopup(false);
    setCurrentNotification(null);
  };

  const handleLockToggle = () => {
    if (isLocked) {
      // Show PIN modal to unlock
      setShowPinModal(true);
      setPinInput('');
      setPinError('');
    } else {
      // Lock the page
      setIsLocked(true);
    }
  };

  const handlePinSubmit = async () => {
    try {
      await axios.post(`${API}/auth/verify-pin`, { pin: pinInput });
      setIsLocked(false);
      setShowPinModal(false);
      setPinInput('');
      setPinError('');
    } catch (error) {
      setPinError('Incorrect PIN. Please try again.');
    }
  };

  const handlePinCancel = () => {
    setShowPinModal(false);
    setPinInput('');
    setPinError('');
  };

  const scrollToInventory = () => {
    if (inventoryRef.current) {
      inventoryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveTab('available'); // Switch to Available tab
      toast.info('📦 Scrolling to inventory...', { duration: 2000 });
    }
  };

  const scrollToPickedUp = () => {
    if (inventoryRef.current) {
      inventoryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveTab('picked_up'); // Switch to Picked Up tab
      toast.info('✅ Showing picked up packages...', { duration: 2000 });
    }
  };

  const scrollToTotal = () => {
    if (inventoryRef.current) {
      inventoryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveTab('total'); // Switch to Total tab
      toast.info('📋 Showing all packages...', { duration: 2000 });
    }
  };

  const handleClearAllInventory = async () => {
    try {
      // Archive only non-archived packages
      const packagesToArchive = packages.filter(p => !p.archived);
      
      if (packagesToArchive.length === 0) {
        toast.info('No packages to archive');
        setShowClearAllDialog(false);
        return;
      }
      
      const archivePromises = packagesToArchive.map(pkg => 
        adminApi.post(`${API}/packages/${pkg.id}/archive`)
      );
      
      await Promise.all(archivePromises);
      
      setShowClearAllDialog(false);
      toast.success(`🗑️ ${packagesToArchive.length} packages moved to history!`);
      fetchPackages(); // Refresh
      fetchStats();
    } catch (error) {
      console.error('Error archiving inventory:', error);
      toast.error('Failed to move packages to history');
    }
  };

  const handleClearActiveInventory = async () => {
    try {
      // Archive only available (not picked up) packages
      const availablePackages = packages.filter(p => !p.is_picked_up && !p.archived);
      
      if (availablePackages.length === 0) {
        toast.info('No available packages to archive');
        return;
      }
      
      const archivePromises = availablePackages.map(pkg => 
        adminApi.post(`${API}/packages/${pkg.id}/archive`)
      );
      
      await Promise.all(archivePromises);
      
      toast.success(`✅ ${availablePackages.length} available packages moved to history!`);
      fetchPackages();
      fetchStats();
    } catch (error) {
      console.error('Error archiving active inventory:', error);
      toast.error('Failed to archive packages');
    }
  };

  const handleClearPickedUp = async () => {
    try {
      // Archive (not delete) only picked up packages
      const pickedUpPackages = packages.filter(pkg => pkg.is_picked_up && !pkg.archived);
      
      if (pickedUpPackages.length === 0) {
        toast.info('No picked up packages to archive');
        return;
      }
      
      const archivePromises = pickedUpPackages.map(pkg => 
        adminApi.post(`${API}/packages/${pkg.id}/archive`)
      );
      
      await Promise.all(archivePromises);
      
      toast.success(`✅ ${pickedUpPackages.length} picked up packages moved to history!`);
      fetchPackages();
      fetchStats();
    } catch (error) {
      console.error('Error archiving picked up packages:', error);
      toast.error('Failed to archive packages');
    }
  };

  const handleClearOldPackages = async () => {
    try {
      // Archive (not delete) only 12+ day old packages
      if (oldPackages.length === 0) {
        toast.info('No old packages to archive');
        return;
      }
      
      const archivePromises = oldPackages.map(pkg => 
        adminApi.post(`${API}/packages/${pkg.id}/archive`)
      );
      
      await Promise.all(archivePromises);
      
      setShowOldPackagesWarning(false);
      toast.success(`✅ ${oldPackages.length} old packages moved to history!`);
      setOldPackages([]);
      fetchPackages();
      fetchStats();
    } catch (error) {
      console.error('Error archiving old packages:', error);
      toast.error('Failed to archive packages');
    }
  };

  const handleClearHistory = async () => {
    try {
      await adminApi.delete(`${API}/packages/history/clear`);
      setShowClearHistoryDialog(false);
      toast.success('🗑️ History cleared permanently!');
      fetchHistory(); // Refresh history
    } catch (error) {
      console.error('Error clearing history:', error);
      toast.error('Failed to clear history');
    }
  };

  const handle3rdPartyPickup = async () => {
    try {
      // Validate required fields
      if (!thirdPartyForm.boxNumber || !thirdPartyForm.pickerName) {
        toast.error('Box number and picker name are required!');
        return;
      }

      // Find package by box number (use prefill if available, otherwise search)
      let packageToPickup;
      if (thirdPartyPrefillData && thirdPartyPrefillData.packageId) {
        packageToPickup = packages.find(p => p.id === thirdPartyPrefillData.packageId);
      } else {
        packageToPickup = packages.find(p => p.box_number === parseInt(thirdPartyForm.boxNumber));
      }
      
      if (!packageToPickup) {
        toast.error(`No package found with box number ${thirdPartyForm.boxNumber}`);
        return;
      }

      if (packageToPickup.is_picked_up) {
        toast.error('This package has already been picked up!');
        return;
      }

      // Prepare 3rd party picker info
      const pickerInfo = {
        name: thirdPartyForm.pickerName,
        id: thirdPartyForm.pickerId || null,
        phone: thirdPartyForm.pickerPhone || null,
        type: '3rd_party',
        original_recipient: packageToPickup.recipient_name
      };

      // Mark package as picked up by 3rd party
      await adminApi.patch(`${API}/packages/${packageToPickup.id}`, {
        is_picked_up: true,
        picked_up_by: pickerInfo
      });

      toast.success(`Package #${thirdPartyForm.boxNumber} picked up by ${thirdPartyForm.pickerName}!`);
      celebratePickup();
      soundEffects.playSuccess();
      flashCartoon('checky');
      setShow3rdPartyPickupModal(false);
      
      // Reset form and prefill data
      setThirdPartyForm({
        boxNumber: '',
        pickerName: '',
        pickerId: '',
        pickerPhone: ''
      });
      setThirdPartyPrefillData(null);
      
      // Refresh packages
      fetchPackages();
    } catch (error) {
      console.error('Error processing 3rd party pickup:', error);
      toast.error('Failed to process pickup');
    }
  };

  // NEW: Search package by box number for manual entry
  const [searchedPackage, setSearchedPackage] = useState(null);
  
  const handleBoxNumberSearch = (boxNum) => {
    if (!boxNum) {
      setSearchedPackage(null);
      return;
    }
    
    const pkg = packages.find(p => p.box_number === parseInt(boxNum) && !p.is_picked_up);
    setSearchedPackage(pkg || null);
  };

  // Handle management button PIN verification
  const handleManagementPinSubmit = async () => {
    try {
      await axios.post(`${API}/auth/verify-pin`, { pin: managementPinInput });
      setShowManagementPinModal(false);
      setManagementPinInput('');
      setManagementPinError('');
      
      // Execute the pending action
      if (pendingAction === 'set_box') {
        setShowCustomNumberDialog(true);
      } else if (pendingAction === 'reset') {
        setShowResetConfirmDialog(true);
      } else if (pendingAction === 'clear_all') {
        setShowClearAllDialog(true);
      }
      
      setPendingAction(null);
    } catch (error) {
      setManagementPinError('Incorrect PIN. Try again.');
      setManagementPinInput('');
    }
  };

  // Open PIN modal for management actions
  const requestManagementAction = (action) => {
    setPendingAction(action);
    setShowManagementPinModal(true);
    setManagementPinInput('');
    setManagementPinError('');
  };

  const fetchPackages = async () => {
    try {
      // Get ALL packages (including picked up) but exclude archived
      const response = await axios.get(`${API}/packages?active_only=false`);
      setPackages(response.data);
      setFilteredPackages(response.data);
      calculateEnhancedStats(response.data);
      
      // Check for 12+ day old packages
      checkOldPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load packages');
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/packages/history/all`);
      setHistoryPackages(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load history');
    }
  };

  const fetchRatingStats = async () => {
    try {
      const response = await adminApi.get(`${API}/ratings/stats`);
      setRatingStats(response.data);
    } catch (error) {
      console.error('Error fetching rating stats:', error);
    }
  };

  const flashCartoon = (character) => {
    setShowCartoon(character);
    setTimeout(() => setShowCartoon(null), 2500);
  };

  // Check for packages 12+ days old and show warning once per day
  const checkOldPackages = (pkgs) => {
    const now = new Date();
    const todayDateString = now.toDateString();
    
    // Check if we already showed warning today
    if (lastWarningDate === todayDateString) {
      return;
    }
    
    // Find packages 12+ days old that are not picked up
    const veryOldPackages = pkgs.filter(pkg => {
      if (pkg.is_picked_up) return false;
      const days = Math.ceil((now - new Date(pkg.date_added)) / (1000 * 60 * 60 * 24));
      return days >= 12;
    });
    
    if (veryOldPackages.length > 0) {
      setOldPackages(veryOldPackages);
      setShowOldPackagesWarning(true);
      setLastWarningDate(todayDateString);
    }
  };

  const calculateEnhancedStats = (pkgs) => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    // Count packages by time period
    const todayPackages = pkgs.filter(p => new Date(p.date_added) >= todayStart).length;
    const weekPackages = pkgs.filter(p => new Date(p.date_added) >= weekStart).length;
    const monthPackages = pkgs.filter(p => new Date(p.date_added) >= monthStart).length;

    // Count picked up today
    const pickedUpToday = pkgs.filter(p => 
      p.is_picked_up && p.pickup_date && new Date(p.pickup_date) >= todayStart
    ).length;

    // Calculate average pickup time (in days)
    const pickedUpPackages = pkgs.filter(p => p.is_picked_up && p.pickup_date);
    let avgPickupTime = 0;
    if (pickedUpPackages.length > 0) {
      const totalDays = pickedUpPackages.reduce((sum, pkg) => {
        const added = new Date(pkg.date_added);
        const pickedUp = new Date(pkg.pickup_date);
        const diffDays = Math.ceil((pickedUp - added) / (1000 * 60 * 60 * 24));
        return sum + diffDays;
      }, 0);
      avgPickupTime = (totalDays / pickedUpPackages.length).toFixed(1);
    }

    // Count old packages (12+ days) - updated for 2-week policy
    const oldPackages = pkgs.filter(p => {
      if (p.is_picked_up) return false;
      const days = Math.ceil((now - new Date(p.date_added)) / (1000 * 60 * 60 * 24));
      return days >= 12;
    }).length;

    // Carrier breakdown
    const carrierCounts = pkgs.reduce((acc, pkg) => {
      const carrier = pkg.carrier || 'Unknown';
      acc[carrier] = (acc[carrier] || 0) + 1;
      return acc;
    }, {});

    setStats({
      total_packages: pkgs.length,
      active_packages: pkgs.filter(p => !p.is_picked_up).length,
      picked_up_today: pickedUpToday,
      today_packages: todayPackages,
      week_packages: weekPackages,
      month_packages: monthPackages,
      avg_pickup_time: avgPickupTime,
      old_packages: oldPackages,
      carrier_breakdown: carrierCounts
    });
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    applyAllFilters(query, ageFilter, carrierFilter, statusFilter);
  };

  // Helper function to calculate package age in days
  const getPackageAgeDays = (dateAdded) => {
    const now = new Date();
    const added = new Date(dateAdded);
    const diffTime = Math.abs(now - added);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Comprehensive filtering function
  const applyAllFilters = (search, age, carrier, status) => {
    let filtered = [...packages];

    // Search filter (name, box number, tracking, carrier)
    if (search && search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(pkg => 
        pkg.recipient_name.toLowerCase().includes(searchLower) ||
        pkg.box_number.toString().includes(search) ||
        (pkg.carrier && pkg.carrier.toLowerCase().includes(searchLower)) ||
        (pkg.tracking_numbers && pkg.tracking_numbers.some(t => t.toLowerCase().includes(searchLower))) ||
        (pkg.phone && pkg.phone.includes(search))
      );
    }

    // Age filter (updated for 2-week policy)
    if (age !== 'all') {
      filtered = filtered.filter(pkg => {
        const days = getPackageAgeDays(pkg.date_added);
        if (age === 'today') return days <= 1;
        if (age === 'week') return days <= 7;
        if (age === 'old') return days >= 12;
        return true;
      });
    }

    // Carrier filter
    if (carrier !== 'all') {
      filtered = filtered.filter(pkg => pkg.carrier === carrier);
    }

    // Status filter
    if (status !== 'all') {
      filtered = filtered.filter(pkg => 
        status === 'available' ? !pkg.is_picked_up : pkg.is_picked_up
      );
    }

    setFilteredPackages(filtered);
  };

  // Re-apply filters when packages or filter criteria change
  useEffect(() => {
    applyAllFilters(searchQuery, ageFilter, carrierFilter, statusFilter);
  }, [packages, ageFilter, carrierFilter, statusFilter]);

  const handlePackageAdded = (newPackage) => {
    setPackages(prev => [newPackage, ...prev]);
    setFilteredPackages(prev => [newPackage, ...prev]);
    fetchStats();
    soundEffects.playDing();
    flashCartoon('boxy');
    toast.success(`Package added - Box #${newPackage.box_number}`);
  };

  const handlePackageUpdated = (updatedPackage) => {
    setPackages(prev => prev.map(pkg => 
      pkg.id === updatedPackage.id ? updatedPackage : pkg
    ));
    setFilteredPackages(prev => prev.map(pkg => 
      pkg.id === updatedPackage.id ? updatedPackage : pkg
    ));
    fetchStats();
  };

  const handlePackageDeleted = (deletedId) => {
    setPackages(prev => prev.filter(pkg => pkg.id !== deletedId));
    setFilteredPackages(prev => prev.filter(pkg => pkg.id !== deletedId));
    fetchStats();
    toast.success('Package moved to history');
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPackages(), fetchStats(), fetchRatingStats()]);
      setLoading(false);
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="loading-spinner"></div>
        <span className="ml-3 text-gray-600">Loading packages...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto" data-testid="staff-dashboard">
      <CartoonStyles />
      
      {/* Cartoon Character Popup */}
      {showCartoon && (
        <div style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          zIndex: 9999, pointerEvents: 'none',
          animation: 'cartoonPop 0.4s ease-out forwards'
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)', borderRadius: '24px', padding: '24px 32px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center',
            animation: 'cartoonBounce 1.5s ease-in-out'
          }}>
            {showCartoon === 'boxy' && <>
              <Boxy size={100} animate={true} />
              <p style={{ margin: '8px 0 0', fontWeight: 'bold', color: '#D2691E', fontSize: '1.1rem' }}>Package Added!</p>
            </>}
            {showCartoon === 'checky' && <>
              <Checky size={100} />
              <p style={{ margin: '8px 0 0', fontWeight: 'bold', color: '#27AE60', fontSize: '1.1rem' }}>Picked Up!</p>
            </>}
            {showCartoon === 'noti' && <>
              <Noti size={100} />
              <p style={{ margin: '8px 0 0', fontWeight: 'bold', color: '#F39C12', fontSize: '1.1rem' }}>New Notification!</p>
            </>}
          </div>
        </div>
      )}
      {/* Header First */}
      <div className="text-center mb-6">
        <div className="relative inline-block">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            <i className="fas fa-shield-alt mr-4 text-yellow-500"></i>
            Ashop Admin Portal
          </h1>
          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-indigo-500 to-pink-500 rounded-full"></div>
        </div>
        <p className="text-gray-600 mt-4 text-lg">
          Admin Management • Package Processing • Business Analytics • Staff Operations
        </p>
      </div>

      {/* Navigation Buttons Below Header */}
      <div className="flex justify-between items-start mb-4 gap-4">
        <div className="flex flex-wrap items-center gap-3 max-w-[75%]">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
          >
            <i className="fas fa-home"></i>
            <span>Home</span>
          </button>
          <button
            onClick={() => navigate('/users')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl"
          >
            <i className="fas fa-users"></i>
            <span>Users</span>
          </button>
          <button
            onClick={() => navigate('/updates')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-red-600 transition-all shadow-lg hover:shadow-xl"
          >
            <i className="fas fa-star"></i>
            <span>Updates</span>
          </button>
          <button
            onClick={() => navigate('/finances')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl"
            data-testid="finances-nav-btn"
          >
            <i className="fas fa-sack-dollar"></i>
            <span>Finances</span>
          </button>
          <button
            onClick={() => navigate('/inbox')}
            className="relative inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-rose-700 transition-all shadow-lg hover:shadow-xl"
            data-testid="inbox-nav-btn"
          >
            <i className="fas fa-comments"></i>
            <span>Suggestions &amp; Notice</span>
            {inboxUnread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded-full bg-white text-rose-600 text-xs font-black shadow" data-testid="inbox-unread-badge">
                {inboxUnread > 99 ? '99+' : inboxUnread}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/prayer-times')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-700 text-white font-semibold rounded-xl hover:from-teal-700 hover:to-cyan-800 transition-all shadow-lg hover:shadow-xl"
            data-testid="prayer-times-nav-btn"
          >
            <i className="fas fa-mosque"></i>
            <span>Prayer Times</span>
          </button>
          <button
            onClick={() => navigate('/analytics')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
            data-testid="analytics-nav-btn"
          >
            <i className="fas fa-chart-line"></i>
            <span>Analytics</span>
          </button>
          <button
            onClick={() => navigate('/demand-list')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl"
            data-testid="demand-list-nav-btn"
          >
            <i className="fas fa-clipboard-list"></i>
            <span>Demand List</span>
          </button>
          <button
            onClick={() => navigate('/rewards-admin')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-white font-semibold rounded-xl hover:from-yellow-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-xl"
            data-testid="rewards-admin-nav-btn"
          >
            <i className="fas fa-gift"></i>
            <span>Rewards</span>
          </button>
          <button
            onClick={() => navigate('/export')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold rounded-xl hover:from-cyan-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
            data-testid="export-nav-btn"
          >
            <i className="fas fa-file-csv"></i>
            <span>Export</span>
          </button>
          <button
            onClick={() => navigate('/currencies')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-semibold rounded-xl hover:from-yellow-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl"
            data-testid="currencies-nav-btn"
          >
            <i className="fas fa-arrow-right-arrow-left"></i>
            <span>Currency</span>
          </button>
          <button
            onClick={() => navigate('/register')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-fuchsia-700 transition-all shadow-lg hover:shadow-xl"
            data-testid="register-nav-btn"
          >
            <i className="fas fa-cash-register"></i>
            <span>Register</span>
          </button>
          <button
            onClick={() => {
              localStorage.removeItem('adminToken');
              localStorage.removeItem('ashop_admin_token');
              localStorage.removeItem('ashop_admin');
              window.location.href = '/';
            }}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white font-semibold rounded-xl hover:from-red-700 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl"
          >
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </button>
        </div>
        {/* Clock + Lock Button stacked */}
        <div className="flex flex-col items-center gap-3" data-testid="admin-clock-lock-wrapper">
          <AshopClock size={130} showDigital={false} />
          {/* Lock Button */}
          <button
            onClick={handleLockToggle}
            className={`inline-flex items-center space-x-2 px-6 py-3 font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl ${
              isLocked
                ? 'bg-gradient-to-r from-red-600 to-red-800 text-white hover:from-red-700 hover:to-red-900'
                : 'bg-gradient-to-r from-green-600 to-green-800 text-white hover:from-green-700 hover:to-green-900'
            }`}
          >
            <i className={`fas ${isLocked ? 'fa-lock' : 'fa-lock-open'}`}></i>
            <span>{isLocked ? 'Locked' : 'Lock Page'}</span>
          </button>
        </div>
      </div>

      {/* Full-width Printing Services button */}
      <button
        onClick={() => navigate('/print-jobs')}
        className="w-full mb-8 flex items-center justify-between gap-4 px-6 py-5 bg-gradient-to-r from-orange-500 via-pink-500 to-red-500 text-white font-bold rounded-2xl hover:from-orange-600 hover:via-pink-600 hover:to-red-600 transition-all shadow-lg hover:shadow-2xl group"
        data-testid="printing-services-nav-btn"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center text-2xl">
            <i className="fas fa-print"></i>
          </div>
          <div className="text-left">
            <p className="text-xs uppercase tracking-widest opacity-80">Customer Inquiries</p>
            <p className="text-xl font-black">Printing Services</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm opacity-90 hidden sm:inline">View all print orders</span>
          <i className="fas fa-arrow-right text-lg group-hover:translate-x-1 transition-transform"></i>
        </div>
      </button>

      <StatsCards 
        stats={stats} 
        onInventoryClick={scrollToInventory} 
        onPickedUpClick={scrollToPickedUp}
        onTotalClick={scrollToTotal}
      />

      {/* Analytics Dashboard */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Time Period Stats */}
          <div className="card bg-gradient-to-br from-blue-50 to-indigo-50">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              <i className="fas fa-calendar-alt mr-2 text-blue-600"></i>
              Package Activity
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-blue-100">
                <span className="text-gray-600">Today</span>
                <span className="font-bold text-blue-600 text-xl">{stats.today_packages || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-blue-100">
                <span className="text-gray-600">This Week</span>
                <span className="font-bold text-indigo-600 text-xl">{stats.week_packages || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">This Month</span>
                <span className="font-bold text-purple-600 text-xl">{stats.month_packages || 0}</span>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="card bg-gradient-to-br from-green-50 to-emerald-50">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              <i className="fas fa-chart-line mr-2 text-green-600"></i>
              Performance
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-green-100">
                <span className="text-gray-600">Avg Pickup Time</span>
                <span className="font-bold text-green-600 text-xl">{stats.avg_pickup_time || 0} days</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-green-100">
                <span className="text-gray-600">Needs Attention</span>
                <span className={`font-bold text-xl ${stats.old_packages > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {stats.old_packages || 0} {stats.old_packages === 1 ? 'package' : 'packages'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">Active Rate</span>
                <span className="font-bold text-emerald-600 text-xl">
                  {stats.total_packages > 0 ? ((stats.active_packages / stats.total_packages) * 100).toFixed(0) : 0}%
                </span>
              </div>
            </div>
          </div>

          {/* Carrier Breakdown */}
          <div className="card bg-gradient-to-br from-orange-50 to-yellow-50">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              <i className="fas fa-truck mr-2 text-orange-600"></i>
              By Carrier
            </h3>
            <div className="space-y-2">
              {stats.carrier_breakdown && Object.entries(stats.carrier_breakdown)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([carrier, count]) => (
                  <div key={carrier} className="flex justify-between items-center py-2 border-b border-orange-100">
                    <span className="text-gray-600">{carrier}</span>
                    <span className="font-bold text-orange-600 text-xl">{count}</span>
                  </div>
                ))}
              {(!stats.carrier_breakdown || Object.keys(stats.carrier_breakdown).length === 0) && (
                <p className="text-gray-500 text-sm">No carrier data available</p>
              )}
            </div>
          </div>

          {/* Customer Ratings */}
          <div className="card bg-gradient-to-br from-yellow-50 to-amber-50" data-testid="customer-ratings-card">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              <i className="fas fa-star mr-2 text-yellow-500"></i>
              Customer Ratings
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-yellow-100">
                <span className="text-gray-600">Average</span>
                <div className="flex items-center space-x-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className={`fas fa-star text-sm ${
                        star <= Math.round(ratingStats.average) ? 'text-yellow-400' : 'text-gray-300'
                      }`}></i>
                    ))}
                  </div>
                  <span className="font-bold text-yellow-600 text-xl">{ratingStats.average || 0}</span>
                </div>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-yellow-100">
                <span className="text-gray-600">Total Reviews</span>
                <span className="font-bold text-amber-600 text-xl">{ratingStats.count || 0}</span>
              </div>
              {ratingStats.count > 0 && (
                <div className="pt-2 space-y-1">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const key = `${['one', 'two', 'three', 'four', 'five'][star - 1]}_star`;
                    const count = ratingStats[key] || 0;
                    const pct = ratingStats.count > 0 ? (count / ratingStats.count) * 100 : 0;
                    return (
                      <div key={star} className="flex items-center space-x-2 text-xs">
                        <span className="w-4 text-gray-500">{star}</span>
                        <i className="fas fa-star text-yellow-400 text-xs"></i>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-yellow-400 rounded-full" style={{ width: `${pct}%` }}></div>
                        </div>
                        <span className="w-6 text-gray-500 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {ratingStats.count === 0 && (
                <p className="text-gray-500 text-sm">No ratings yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Camera Scanner - Full Width */}
      <div className="mb-8">
        <CameraScanner 
          onPackageAdded={handlePackageAdded}
          managementButtons={
            <>
              {/* Box Number Management Buttons */}
              <button
                onClick={() => requestManagementAction('set_box')}
                className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-all duration-200 flex items-center justify-center space-x-2 min-w-[140px]"
              >
                <i className="fas fa-lock mr-1 text-xs"></i>
                <i className="fas fa-edit"></i>
                <span>Set Box #</span>
              </button>
              
              <button
                onClick={() => requestManagementAction('reset')}
                className="px-4 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-all duration-200 flex items-center justify-center space-x-2 min-w-[140px]"
              >
                <i className="fas fa-lock mr-1 text-xs"></i>
                <i className="fas fa-redo"></i>
                <span>Reset</span>
              </button>
              
              <button
                onClick={() => requestManagementAction('clear_all')}
                className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-all duration-200 flex items-center justify-center space-x-2 min-w-[140px]"
              >
                <i className="fas fa-lock mr-1 text-xs"></i>
                <i className="fas fa-trash-alt"></i>
                <span>Clear All</span>
              </button>
              
              <button
                onClick={() => setShow3rdPartyPickupModal(true)}
                className="px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-all duration-200 flex items-center justify-center space-x-2 min-w-[140px]"
              >
                <i className="fas fa-user-friends"></i>
                <span>3rd Party Pickup</span>
              </button>
            </>
          }
        />
      </div>

      {/* Manual Entry Form - Full Width Below Camera */}
      <div className="mb-8">
        <div className="card">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
            <i className="fas fa-edit mr-3 text-blue-600"></i>
            Manual Package Entry
          </h2>
          <PackageForm onPackageAdded={handlePackageAdded} />
        </div>
      </div>

      {/* Package List - Full Width at Bottom */}
      <div className="card" ref={inventoryRef}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">
            <i className="fas fa-list mr-3 text-blue-600"></i>
            Active Package Inventory
          </h2>
          <div className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
            {filteredPackages.length} packages in inventory
          </div>
        </div>
        
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name, box number, tracking, phone..."
              className="form-input w-full text-lg py-4 px-6 rounded-2xl border-2 border-blue-200 focus:border-blue-500"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              data-testid="package-search-input"
            />
          </div>

          {/* Filter Controls */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Age Quick Filters */}
            <div className="flex items-center space-x-2">
              <span className="text-sm font-semibold text-gray-600">Age:</span>
              <button
                onClick={() => setAgeFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  ageFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setAgeFilter('today')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  ageFilter === 'today'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Today
              </button>
              <button
                onClick={() => setAgeFilter('week')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  ageFilter === 'week'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                This Week
              </button>
              <button
                onClick={() => setAgeFilter('old')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  ageFilter === 'old'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                12+ Days
              </button>
            </div>

            {/* Carrier Filter */}
            <div className="flex items-center space-x-2">
              <span className="text-sm font-semibold text-gray-600">Carrier:</span>
              <select
                value={carrierFilter}
                onChange={(e) => setCarrierFilter(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
              >
                <option value="all">All Carriers</option>
                <option value="UPS">UPS</option>
                <option value="FedEx">FedEx</option>
                <option value="DHL">DHL</option>
                <option value="Mondial">Mondial</option>
                <option value="USPS">USPS</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <span className="text-sm font-semibold text-gray-600">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
              >
                <option value="all">All Packages</option>
                <option value="available">Available Only</option>
                <option value="picked_up">Picked Up Only</option>
              </select>
            </div>

            {/* Clear All Filters */}
            {(searchQuery || ageFilter !== 'all' || carrierFilter !== 'all' || statusFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setAgeFilter('all');
                  setCarrierFilter('all');
                  setStatusFilter('all');
                }}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all"
              >
                <i className="fas fa-times mr-1"></i>
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Tabs for Total / Available / Picked Up */}
        <div className="flex space-x-2 mb-6 border-b border-gray-200 items-center">
          <button
            onClick={() => setActiveTab('total')}
            className={`px-6 py-3 font-semibold text-lg transition-all duration-200 border-b-4 ${
              activeTab === 'total'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="fas fa-list mr-2"></i>
            Total Scanned ({filteredPackages.filter(p => !p.is_picked_up).length})
          </button>
          
          <button
            onClick={() => setActiveTab('available')}
            className={`px-6 py-3 font-semibold text-lg transition-all duration-200 border-b-4 ${
              activeTab === 'available'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="fas fa-box mr-2"></i>
            Available ({filteredPackages.filter(p => !p.is_picked_up).length})
          </button>
          <button
            onClick={() => setActiveTab('picked_up')}
            className={`px-6 py-3 font-semibold text-lg transition-all duration-200 border-b-4 ${
              activeTab === 'picked_up'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="fas fa-check-circle mr-2"></i>
            Picked Up ({filteredPackages.filter(p => p.is_picked_up).length})
          </button>
          <button
            onClick={() => {
              setActiveTab('history');
              fetchHistory(); // Load history when tab is clicked
            }}
            className={`px-6 py-3 font-semibold text-lg transition-all duration-200 border-b-4 ${
              activeTab === 'history'
                ? 'border-orange-600 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="fas fa-history mr-2"></i>
            History ({historyPackages.length})
          </button>
        </div>

        {/* Show different content based on active tab */}
        {activeTab === 'history' ? (
          // History Tab Content
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-600">
                <i className="fas fa-info-circle mr-2"></i>
                Complete archive of all packages (picked up and cleared)
              </p>
              <button
                onClick={() => setShowClearHistoryDialog(true)}
                className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-all duration-200 flex items-center space-x-2"
                disabled={historyPackages.length === 0}
              >
                <i className="fas fa-trash-alt"></i>
                <span>Clear History ({historyPackages.length})</span>
              </button>
            </div>
            
            <PackageList 
              packages={historyPackages}
              onPackageUpdated={fetchHistory}
              onPackageDeleted={fetchHistory}
              isHistory={true}
            />
          </div>
        ) : (
          // Regular Tabs Content
          <div>
            {/* Reset Counter Button - Only show on Total Scanned tab */}
            {activeTab === 'total' && (
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => setShowResetCounterDialog(true)}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-xl hover:from-orange-600 hover:to-red-600 transition-all shadow-lg hover:shadow-xl"
                >
                  <i className="fas fa-redo mr-2"></i>
                  Reset Counter
                </button>
              </div>
            )}
            
            <PackageList 
              packages={filteredPackages.filter(p => {
                if (activeTab === 'total') return !p.is_picked_up; // Show only non-picked-up packages
                if (activeTab === 'available') return !p.is_picked_up;
                if (activeTab === 'picked_up') return p.is_picked_up;
                return true;
              })}
              onPackageUpdated={handlePackageUpdated}
              onPackageDeleted={handlePackageDeleted}
            />
          </div>
        )}
      </div>

      {/* Clear History Confirmation Dialog */}
      {showClearHistoryDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Clear History?</h3>
              <p className="text-gray-600">
                This will <strong>permanently delete</strong> all {historyPackages.length} archived packages. This action cannot be undone.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowClearHistoryDialog(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleClearHistory}
                className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all"
              >
                Clear History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Box Number Initialization Dialog */}
      {showBoxInitDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-box text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Initialize Box Numbering</h3>
              <p className="text-gray-600">What box number should we start from today?</p>
            </div>

            <div className="space-y-4">
              <input
                type="number"
                value={boxStartNumber}
                onChange={(e) => setBoxStartNumber(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleBoxNumberInit()}
                placeholder="Enter starting number (e.g., 1)"
                min="1"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-center text-2xl"
                autoFocus
              />

              <button
                onClick={handleBoxNumberInit}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
              >
                Start Numbering from {boxStartNumber}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Notification Popup */}
      {showNotificationPopup && currentNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-3xl p-6 max-w-2xl w-full mx-4 shadow-2xl animate-bounce-in">
            <div className="text-center mb-5">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3 animate-pulse">
                <i className="fas fa-bell text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">🔔 Customer Ready for Pickup!</h3>
              <p className="text-sm text-gray-600">A customer is at the counter waiting to collect their package</p>
            </div>

            {/* Customer Search Details */}
            {currentNotification.search_details && (
              <div className="mb-4 p-4 bg-blue-50 rounded-xl border-2 border-blue-300">
                <div className="flex items-center space-x-2 mb-2">
                  <i className="fas fa-search text-blue-600"></i>
                  <h4 className="font-bold text-blue-900 text-sm">Customer Searched With:</h4>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {currentNotification.search_details.first_name && (
                    <div className="text-blue-800">
                      <strong>First Name:</strong> {currentNotification.search_details.first_name}
                    </div>
                  )}
                  {currentNotification.search_details.last_name && (
                    <div className="text-blue-800">
                      <strong>Last Name:</strong> {currentNotification.search_details.last_name}
                    </div>
                  )}
                  {currentNotification.search_details.tracking && (
                    <div className="text-blue-800">
                      <strong>Tracking:</strong> {currentNotification.search_details.tracking}
                    </div>
                  )}
                  {currentNotification.search_details.phone && (
                    <div className="text-blue-800">
                      <strong>Phone:</strong> {currentNotification.search_details.phone}
                    </div>
                  )}
                  {currentNotification.search_details.email && (
                    <div className="text-blue-800">
                      <strong>Email:</strong> {currentNotification.search_details.email}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Package Details Found */}
            <div className="space-y-3 bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-xl border-2 border-green-300">
              <div className="flex items-center space-x-2 mb-2">
                <i className="fas fa-package text-green-600"></i>
                <h4 className="font-bold text-green-900 text-sm">Package Found:</h4>
              </div>

              <div className="flex items-center space-x-3">
                <i className="fas fa-user text-blue-600 text-lg"></i>
                <div>
                  <p className="text-xs text-gray-600">Customer Name</p>
                  <p className="text-lg font-bold text-gray-800">{currentNotification.customer_name}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <i className="fas fa-barcode text-purple-600 text-lg"></i>
                <div>
                  <p className="text-xs text-gray-600">Tracking Number</p>
                  <p className="text-sm font-semibold text-gray-800">{currentNotification.tracking_number}</p>
                </div>
              </div>

              {/* Carrier Information */}
              <div className="flex items-center space-x-3">
                <i className="fas fa-truck text-indigo-600 text-lg"></i>
                <div>
                  <p className="text-xs text-gray-600">Carrier</p>
                  <p className="text-sm font-bold text-indigo-800">
                    {currentNotification.carrier || 'Not specified'}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <i className="fas fa-box text-orange-600 text-lg"></i>
                <div>
                  <p className="text-xs text-gray-600">Box Number</p>
                  <p className="text-3xl font-bold text-orange-600">#{currentNotification.box_number}</p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3 mt-5">
              <button
                onClick={async () => {
                  try {
                    // Mark notification as read when cancelled
                    await axios.put(`${API}/notifications/${currentNotification.id}/read`);
                  } catch (error) {
                    console.error('Error marking notification as read:', error);
                  }
                  closeNotificationPopup();
                }}
                className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-bold rounded-xl transition-all duration-300"
              >
                <i className="fas fa-times mr-2"></i>
                Cancel
              </button>
              
              <button
                onClick={async () => {
                  try {
                    // Mark notification as read
                    await axios.put(`${API}/notifications/${currentNotification.id}/read`);
                    
                    // Prepare prefill data for 3rd party pickup modal
                    const prefillData = {
                      packageId: currentNotification.package_id,
                      boxNumber: currentNotification.box_number,
                      customerName: currentNotification.customer_name,
                      trackingNumber: currentNotification.tracking_number,
                      carrier: currentNotification.carrier
                    };
                    
                    setThirdPartyPrefillData(prefillData);
                    setThirdPartyForm({
                      ...thirdPartyForm,
                      boxNumber: currentNotification.box_number.toString()
                    });
                    
                    closeNotificationPopup();
                    setShow3rdPartyPickupModal(true);
                  } catch (error) {
                    console.error('Error opening 3rd party modal:', error);
                  }
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-sm font-bold rounded-xl transition-all duration-300 shadow-lg"
              >
                <i className="fas fa-user-friends mr-2"></i>
                3rd Party
              </button>
              
              <button
                onClick={async () => {
                  try {
                    // Mark notification as read
                    await axios.put(`${API}/notifications/${currentNotification.id}/read`);
                    
                    // Prepare customer info from search details
                    const searchDetails = currentNotification.search_details || {};
                    const customerInfo = {
                      name: `${searchDetails.first_name || ''} ${searchDetails.last_name || ''}`.trim() || currentNotification.customer_name || 'Walk-in Customer',
                      phone: searchDetails.phone || null,
                      email: searchDetails.email || null
                    };
                    
                    // Mark package as picked up WITH customer info
                    await adminApi.patch(`${API}/packages/${currentNotification.package_id}`, {
                      is_picked_up: true,
                      picked_up_by: customerInfo
                    });
                    
                    toast.success(`Package #${currentNotification.box_number} marked as picked up by ${customerInfo.name}!`);
                    celebratePickup();
                    soundEffects.playSuccess();
                    flashCartoon('checky');
                    closeNotificationPopup();
                    
                    // Refresh packages list
                    fetchPackages();
                  } catch (error) {
                    console.error('Error marking as picked up:', error);
                    toast.error('Failed to mark package as picked up');
                  }
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-sm font-bold rounded-xl transition-all duration-300 shadow-lg"
              >
                <i className="fas fa-check-circle mr-2"></i>
                Customer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden audio element for notification sound */}
      <audio ref={audioRef} preload="auto">
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZSA0PVKzn77BfGAg+ltryxnMpBSuAzPLaizsIGGS57OihUhELTqXh8bllHAU2jdXywn0vBSh+zPDejj4KFmC26+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThALTKPg8bllHAU2jdTywn0vBSh+zO/ejj4KFmC16+mjThAL" type="audio/wav" />
      </audio>

      {/* PIN Modal */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-lock text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Enter PIN to Unlock</h3>
              <p className="text-gray-600">Please enter the 4-digit PIN to unlock the admin portal</p>
            </div>

            <div className="space-y-4">
              <input
                type="password"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePinSubmit()}
                placeholder="Enter PIN"
                maxLength="4"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-center text-2xl tracking-widest"
                autoFocus
              />
              
              {pinError && (
                <div className="text-red-600 text-sm text-center font-semibold">
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  {pinError}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={handlePinCancel}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-400 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePinSubmit}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-900 transition-all duration-300"
                >
                  Unlock
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Box Number Dialog */}
      {showCustomNumberDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-edit text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Set Custom Box Number</h3>
              <p className="text-gray-600">Choose the next box number to use</p>
            </div>

            <div className="space-y-4">
              <input
                type="number"
                value={customBoxNumber}
                onChange={(e) => setCustomBoxNumber(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSetCustomNumber()}
                placeholder="Enter box number (e.g., 100)"
                min="1"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-center text-2xl"
                autoFocus
              />

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowCustomNumberDialog(false);
                    setCustomBoxNumber('');
                  }}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-400 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSetCustomNumber}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-900 transition-all duration-300"
                >
                  Set Number
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      {showResetConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-exclamation-triangle text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Reset Box Numbering?</h3>
              <p className="text-gray-600 mb-4">This will reset the box numbering back to 1.</p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 text-left">
                <p className="text-sm text-yellow-800">
                  <i className="fas fa-info-circle mr-2"></i>
                  <strong>Note:</strong> The next scanned package will be assigned box #1. This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowResetConfirmDialog(false)}
                className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-400 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleResetBoxNumber}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-800 text-white font-semibold rounded-xl hover:from-red-700 hover:to-red-900 transition-all duration-300"
              >
                Reset to 1
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Reset Total Scanned Counter Dialog */}
      {showResetCounterDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-redo text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Reset Counter</h3>
              <p className="text-gray-600">Current counter: <strong className="text-indigo-600">#{boxNumberState.current || '?'}</strong></p>
            </div>

            {/* Set Custom Number */}
            <div className="border-2 border-indigo-300 rounded-2xl p-6 mb-4 bg-indigo-50">
              <h4 className="text-lg font-bold text-indigo-800 mb-3">
                <i className="fas fa-hashtag mr-2"></i>
                Set counter to any number
              </h4>
              <p className="text-sm text-gray-600 mb-4">Type the number you want the next package to start from</p>
              <div className="flex space-x-3">
                <input
                  type="number"
                  min="1"
                  value={resetCustomNumber}
                  onChange={(e) => setResetCustomNumber(e.target.value)}
                  placeholder="e.g. 1"
                  className="flex-1 px-4 py-3 border-2 border-indigo-300 rounded-xl text-xl font-bold text-center focus:border-indigo-500 focus:outline-none"
                  data-testid="reset-custom-number-input"
                />
                <button
                  data-testid="set-custom-number-btn"
                  onClick={async () => {
                    const num = parseInt(resetCustomNumber);
                    if (!num || num < 1) {
                      toast.error('Please enter a valid number (1 or higher)');
                      return;
                    }
                    try {
                      await adminApi.post(`${API}/box-number/reset?start_number=${num}`);
                      await adminApi.post(`${API}/total-scanned/reset?start_number=${num - 1}`);
                      setShowResetCounterDialog(false);
                      setBoxNumberState({ current: num, initialized: true });
                      setResetCustomNumber('');
                      toast.success(`Counter set to #${num}! Next package will be #${num}`);
                      soundEffects.playSuccess();
                      fetchStats();
                    } catch (error) {
                      console.error('Error:', error);
                      toast.error('Failed to set counter');
                    }
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all"
                >
                  Set
                </button>
              </div>
            </div>

            {/* Delete Picked Up & Reset */}
            <div className="border-2 border-red-300 rounded-2xl p-6 mb-4 bg-red-50">
              <h4 className="text-lg font-bold text-red-800 mb-2">
                <i className="fas fa-broom mr-2"></i>
                Clean Up & Reset
              </h4>
              <p className="text-sm text-gray-600 mb-2">Deletes only picked-up packages. <strong className="text-green-700">Active packages stay untouched.</strong></p>
              <p className="text-sm text-gray-600 mb-4">Counter resets to the number you set above. New scans will auto-skip any box numbers still in use.</p>
              <button
                data-testid="delete-picked-up-reset-btn"
                onClick={async () => {
                  const num = parseInt(resetCustomNumber) || 1;
                  try {
                    await adminApi.delete(`${API}/packages/picked-up/clear`);
                    await adminApi.post(`${API}/box-number/reset?start_number=${num}`);
                    await adminApi.post(`${API}/total-scanned/reset?start_number=${num - 1}`);
                    setShowResetCounterDialog(false);
                    setBoxNumberState({ current: num, initialized: true });
                    setResetCustomNumber('');
                    toast.success(`Picked-up packages removed! Counter set to #${num}. Active packages kept.`);
                    quickBurst();
                    soundEffects.playWhoosh();
                    fetchPackages();
                    fetchStats();
                  } catch (error) {
                    console.error('Error:', error);
                    toast.error('Failed to clean up. Please try again.');
                  }
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold rounded-xl hover:from-red-700 hover:to-red-800 transition-all"
              >
                <i className="fas fa-broom mr-2"></i>
                Delete Picked-Up & Reset to #{parseInt(resetCustomNumber) || 1}
              </button>
            </div>

            {/* Cancel Button */}
            <button
              onClick={() => { setShowResetCounterDialog(false); setResetCustomNumber(''); }}
              className="w-full px-6 py-3 bg-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-400 transition-all"
            >
              <i className="fas fa-times mr-2"></i>
              Cancel
            </button>
          </div>
        </div>
      )}


      {/* Clear Inventory Confirmation Dialog */}
      {showClearAllDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-red-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-trash-alt text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Choose What to Delete</h3>
              <p className="text-gray-600 mb-4">Select which packages you want to remove from inventory</p>
            </div>

            {/* Three Delete Options */}
            <div className="space-y-3 mb-6">
              {/* Option 1: Total Scanned Packages */}
              <button
                onClick={handleClearAllInventory}
                className="w-full p-4 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <div className="font-bold text-lg">Total Scanned Packages</div>
                    <div className="text-sm opacity-90">Delete ALL {packages.length} packages (available + picked up)</div>
                  </div>
                  <i className="fas fa-trash text-2xl"></i>
                </div>
              </button>

              {/* Option 2: Active Inventory */}
              <button
                onClick={handleClearActiveInventory}
                className="w-full p-4 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <div className="font-bold text-lg">Active Inventory</div>
                    <div className="text-sm opacity-90">Delete {packages.filter(p => !p.is_picked_up).length} available packages (not picked up)</div>
                  </div>
                  <i className="fas fa-box text-2xl"></i>
                </div>
              </button>

              {/* Option 3: Picked Up Today */}
              <button
                onClick={handleClearPickedUp}
                className="w-full p-4 bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <div className="font-bold text-lg">Picked Up Packages</div>
                    <div className="text-sm opacity-90">Delete {packages.filter(p => p.is_picked_up).length} picked up packages</div>
                  </div>
                  <i className="fas fa-check-circle text-2xl"></i>
                </div>
              </button>
            </div>

            {/* Warning */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="text-sm text-yellow-800">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                <strong>Warning:</strong> This action cannot be undone!
              </p>
            </div>

            {/* Cancel Button */}
            <button
              onClick={() => setShowClearAllDialog(false)}
              className="w-full px-6 py-3 bg-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-400 transition-all duration-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Welcome Popup removed — welcome message now appears on Matrix loader */}

      {/* Old Packages Warning Modal (12+ days) */}
      {showOldPackagesWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl p-8 max-w-3xl w-full shadow-2xl transform animate-slideUp max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <i className="fas fa-exclamation-triangle text-white text-3xl"></i>
              </div>
              
              <h3 className="text-3xl font-bold text-gray-800 mb-2">
                ⚠️ Old Packages Alert!
              </h3>
              
              <p className="text-gray-600 mb-2">
                You have {oldPackages.length} package{oldPackages.length > 1 ? 's' : ''} that {oldPackages.length > 1 ? 'are' : 'is'} 12+ days old.
              </p>
              
              <p className="text-sm text-red-600 font-semibold">
                Policy: Packages must be picked up within 2 weeks (14 days)
              </p>
            </div>

            {/* List of old packages */}
            <div className="bg-red-50 rounded-2xl p-6 mb-6 max-h-60 overflow-y-auto">
              <h4 className="font-bold text-red-800 mb-3 flex items-center">
                <i className="fas fa-list mr-2"></i>
                Packages Requiring Action:
              </h4>
              <div className="space-y-2">
                {oldPackages.map((pkg, index) => (
                  <div key={pkg.id} className="bg-white rounded-lg p-3 border-l-4 border-red-500 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-gray-800">
                          #{pkg.box_number} - {pkg.recipient_name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {pkg.tracking_numbers && pkg.tracking_numbers.length > 0 && (
                            <span>Tracking: {pkg.tracking_numbers[0]} • </span>
                          )}
                          <span className="text-red-600 font-medium">
                            {Math.ceil((new Date() - new Date(pkg.date_added)) / (1000 * 60 * 60 * 24))} days old
                          </span>
                        </div>
                      </div>
                      <div className="text-2xl">🔴</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => {
                  setShowOldPackagesWarning(false);
                  toast.info('Reminder set - will alert again tomorrow');
                }}
                className="px-6 py-4 bg-gray-200 hover:bg-gray-300 text-gray-800 text-lg font-bold rounded-xl transition-all duration-300"
              >
                <i className="fas fa-clock mr-2"></i>
                Remind Me Later
              </button>
              
              <button
                onClick={() => {
                  setShowOldPackagesWarning(false);
                  // Set filters to show 12+ day old packages
                  setAgeFilter('old');
                  setStatusFilter('available');
                  // Scroll to inventory
                  setTimeout(() => {
                    if (inventoryRef.current) {
                      inventoryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                  toast.info(`Showing ${oldPackages.length} old packages in inventory`);
                }}
                className="px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white text-lg font-bold rounded-xl transition-all duration-300 shadow-lg"
              >
                <i className="fas fa-list mr-2"></i>
                Show List
              </button>
              
              <button
                onClick={handleClearOldPackages}
                className="px-6 py-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white text-lg font-bold rounded-xl transition-all duration-300 shadow-lg"
              >
                <i className="fas fa-trash-alt mr-2"></i>
                Clear All
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              This warning appears once per day for packages 12+ days old
            </p>
          </div>
        </div>
      )}

      {/* 3rd Party Pickup Modal */}
      {show3rdPartyPickupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-user-friends text-white text-2xl"></i>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                3rd Party Pickup
              </h3>
              
              <p className="text-gray-600 text-sm">
                {thirdPartyPrefillData 
                  ? 'Customer requested someone else to pick up their package'
                  : 'Manually enter details when someone else picks up a package'}
              </p>
            </div>

            {/* Show Package Info if prefilled from notification */}
            {thirdPartyPrefillData && (
              <div className="mb-6 p-6 bg-blue-50 rounded-2xl border-2 border-blue-300">
                <div className="flex items-center space-x-3 mb-4">
                  <i className="fas fa-package text-blue-600 text-xl"></i>
                  <h4 className="font-bold text-blue-900 text-lg">Package Information:</h4>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="text-blue-800">
                    <strong>Box #:</strong> {thirdPartyPrefillData.boxNumber}
                  </div>
                  <div className="text-blue-800">
                    <strong>Customer:</strong> {thirdPartyPrefillData.customerName}
                  </div>
                  <div className="text-blue-800">
                    <strong>Carrier:</strong> {thirdPartyPrefillData.carrier}
                  </div>
                  <div className="text-blue-800">
                    <strong>Tracking:</strong> {thirdPartyPrefillData.trackingNumber}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Box Number - with search for manual mode */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Box Number *
                </label>
                <input
                  type="number"
                  value={thirdPartyForm.boxNumber}
                  onChange={(e) => {
                    setThirdPartyForm({ ...thirdPartyForm, boxNumber: e.target.value });
                    if (!thirdPartyPrefillData) {
                      handleBoxNumberSearch(e.target.value);
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
                  placeholder="Enter box number"
                  disabled={thirdPartyPrefillData !== null}
                  autoFocus={!thirdPartyPrefillData}
                />
                {!thirdPartyPrefillData && searchedPackage && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                    <i className="fas fa-check-circle text-green-600 mr-2"></i>
                    <strong>Found:</strong> {searchedPackage.recipient_name} - {searchedPackage.carrier || 'No carrier'} - Tracking: {searchedPackage.tracking_numbers?.[0] || 'N/A'}
                  </div>
                )}
                {!thirdPartyPrefillData && thirdPartyForm.boxNumber && !searchedPackage && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <i className="fas fa-exclamation-circle mr-2"></i>
                    No available package found with this box number
                  </div>
                )}
              </div>

              {/* Picker Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Picker's Name *
                </label>
                <input
                  type="text"
                  value={thirdPartyForm.pickerName}
                  onChange={(e) => setThirdPartyForm({ ...thirdPartyForm, pickerName: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Full name of person picking up"
                  autoFocus={thirdPartyPrefillData !== null}
                />
              </div>

              {/* Picker ID (Optional) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  ID Number <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={thirdPartyForm.pickerId}
                  onChange={(e) => setThirdPartyForm({ ...thirdPartyForm, pickerId: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Driver's license, passport, etc."
                />
              </div>

              {/* Picker Phone (Optional) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <input
                  type="tel"
                  value={thirdPartyForm.pickerPhone}
                  onChange={(e) => setThirdPartyForm({ ...thirdPartyForm, pickerPhone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="+31 6 12 34 56 78"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShow3rdPartyPickupModal(false);
                  setThirdPartyForm({ boxNumber: '', pickerName: '', pickerId: '', pickerPhone: '' });
                  setThirdPartyPrefillData(null);
                  setSearchedPackage(null);
                }}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handle3rdPartyPickup}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all"
              >
                <i className="fas fa-check mr-2"></i>
                Process Pickup
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              * Required fields
            </p>
          </div>
        </div>
      )}

      {/* Management PIN Modal */}
      {showManagementPinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-slideUp">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-lock text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                🔒 PIN Required
              </h3>
              <p className="text-gray-600">
                Enter PIN to access management functions
              </p>
            </div>

            <div className="mb-6">
              <input
                type="password"
                value={managementPinInput}
                onChange={(e) => setManagementPinInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleManagementPinSubmit()}
                className={`w-full px-6 py-4 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-4 transition-all ${
                  managementPinError
                    ? 'border-red-500 focus:ring-red-200 bg-red-50'
                    : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
                }`}
                placeholder="Enter PIN"
                maxLength="4"
                autoFocus
              />
              {managementPinError && (
                <p className="text-red-600 text-sm mt-2 text-center font-semibold">
                  <i className="fas fa-exclamation-circle mr-1"></i>
                  {managementPinError}
                </p>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowManagementPinModal(false);
                  setManagementPinInput('');
                  setManagementPinError('');
                  setPendingAction(null);
                }}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 font-semibold rounded-xl hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleManagementPinSubmit}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all"
              >
                Unlock
              </button>
            </div>

            <p className="text-xs text-gray-400 text-center mt-4">
              Protected function - PIN required
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffDashboard;